'use client';

// ── Root error boundary (2026-08-12) ────────────────────────────────────────
// Catches errors that escape the route boundary (including ones thrown in
// the root layout). Replaces the whole document, so it must render its own
// <html>/<body> and can't rely on globals.css — styles are inline. Same
// stale-chunk auto-reload as app/error.tsx.
//
// This is the page that has to render when everything else failed — including
// when a stale deploy means the CSS chunk itself 404s. So it imports nothing,
// uses no shared primitive and no CSS Module, and carries its brand values as
// literals mirroring src/app/globals.css. The no-restricted-syntax disables
// below are deliberate for that reason.

import { useEffect } from 'react';

function isStaleChunkError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError'
    || /Loading chunk .* failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
      error.message ?? '',
    )
  );
}

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error('Global error boundary:', error);
    if (!isStaleChunkError(error)) return;
    try {
      const key = 'ss:chunk-reload-at';
      const last = Number(sessionStorage.getItem(key) ?? 0);
      if (Date.now() - last < 60_000) return;
      sessionStorage.setItem(key, String(Date.now()));
    } catch { /* reload anyway */ }
    window.location.reload();
  }, [error]);

  const stale = isStaleChunkError(error);

  return (
    <html lang="en">
      <body
        // eslint-disable-next-line no-restricted-syntax -- renders outside the app shell
        style={{
          margin: 0,
          // mirrors --font-body / --background / --foreground in globals.css
          fontFamily: "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          background: '#f8fafc',
          color: '#0f172a',
        }}
      >
        <div
          // eslint-disable-next-line no-restricted-syntax -- renders outside the app shell
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <h2
            // eslint-disable-next-line no-restricted-syntax -- renders outside the app shell
            style={{ fontSize: '1.15rem', fontWeight: 600, margin: 0 }}
          >
            {stale ? 'This page is out of date' : 'Something went wrong'}
          </h2>
          <p
            // eslint-disable-next-line no-restricted-syntax -- renders outside the app shell
            style={{ fontSize: '0.9rem', color: '#64748b', maxWidth: 420, margin: 0 }}
          >
            {stale
              ? 'The app was updated while this tab was open. Reloading picks up the new version — nothing was lost.'
              : 'The error was logged to the browser console. Reload to continue.'}
          </p>
          {/* eslint-disable-next-line no-restricted-syntax -- renders outside the app shell */}
          <button
            type="button"
            onClick={() => window.location.reload()}
            // eslint-disable-next-line no-restricted-syntax -- renders outside the app shell
            style={{
              padding: '0.5rem 1rem',
              borderRadius: 8,
              border: 'none',
              background: 'hsl(192, 100%, 27%)',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Reload page
          </button>
        </div>
      </body>
    </html>
  );
}
